// ✅ Função helper para garantir que valores do theme sejam sempre strings válidas
const safeThemeValue = (value, defaultValue = '') => {
  if (value == null || value === undefined) return defaultValue;
  try {
    const str = String(value).trim();
    return str || defaultValue;
  } catch (e) {
    return defaultValue;
  }
};

// ✅ Função helper para fazer deep clone preservando funções
const deepClonePreservingFunctions = (obj, depth = 0) => {
  if (depth > 15) return obj; // Limite de profundidade
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  
  if (typeof obj === 'function') return obj; // Preservar funções
  
  if (Array.isArray(obj)) {
    return obj.map(item => deepClonePreservingFunctions(item, depth + 1));
  }
  
  const cloned = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const val = obj[key];
      if (typeof val === 'function') {
        // Preservar funções
        cloned[key] = val;
      } else if (typeof val === 'object' && val !== null) {
        cloned[key] = deepClonePreservingFunctions(val, depth + 1);
      } else {
        cloned[key] = val;
      }
    }
  }
  return cloned;
};

// ✅ Função helper para sanitizar recursivamente todas as opções do ApexCharts
export const sanitizeApexOptions = (options) => {
  if (!options || typeof options !== 'object') return {};
  
  try {
    // Deep clone preservando funções
    let sanitized = deepClonePreservingFunctions(options);
    
    // ✅ NOVO: Remover TODOS os valores undefined/null recursivamente ANTES de qualquer processamento
    const removeUndefinedNull = (obj, depth = 0) => {
      if (depth > 15) return obj; // Limite de profundidade
      if (!obj || typeof obj !== 'object') return obj;
      
      if (Array.isArray(obj)) {
        return obj.map(item => {
          if (item == null || item === undefined) return '';
          if (typeof item === 'object') return removeUndefinedNull(item, depth + 1);
          return item;
        }).filter(item => item !== null && item !== undefined);
      }
      
      const cleaned = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const val = obj[key];
          if (val === undefined || val === null) {
            // Não adicionar campos undefined/null
            continue;
          }
          if (typeof val === 'object') {
            cleaned[key] = removeUndefinedNull(val, depth + 1);
          } else {
            cleaned[key] = val;
          }
        }
      }
      return cleaned;
    };
    
    sanitized = removeUndefinedNull(sanitized);
    
    // ✅ CRÍTICO: Sanitizar xaxis.categories (pode conter undefined)
    if (sanitized.xaxis?.categories && Array.isArray(sanitized.xaxis.categories)) {
      sanitized.xaxis.categories = sanitized.xaxis.categories.map(cat => {
        if (cat == null || cat === undefined) return '';
        try {
          const str = String(cat).trim();
          return str || '';
        } catch (e) {
          return '';
        }
      }).filter(cat => cat !== '' && cat != null && cat !== undefined);
    }
    
    // ✅ CRÍTICO: Sanitizar labels (pode conter undefined)
    if (sanitized.labels && Array.isArray(sanitized.labels)) {
      sanitized.labels = sanitized.labels.map(label => {
        if (label == null || label === undefined) return '';
        try {
          const str = String(label).trim();
          return str || '';
        } catch (e) {
          return '';
        }
      }).filter(label => label !== '' && label != null && label !== undefined);
    }
    
    // ✅ CRÍTICO: Sanitizar tooltip.y.formatter (pode retornar undefined)
    if (sanitized.tooltip?.y?.formatter && typeof sanitized.tooltip.y.formatter === 'function') {
      const originalFormatter = sanitized.tooltip.y.formatter;
      sanitized.tooltip.y.formatter = function(val) {
        try {
          const result = originalFormatter.call(this, val);
          if (result == null || result === undefined) return '';
          return String(result).trim() || '';
        } catch (e) {
          return '';
        }
      };
    }
    
    // ✅ CRÍTICO: Sanitizar tooltip.y.title.formatter (pode retornar undefined)
    if (sanitized.tooltip?.y?.title?.formatter && typeof sanitized.tooltip.y.title.formatter === 'function') {
      const originalFormatter = sanitized.tooltip.y.title.formatter;
      sanitized.tooltip.y.title.formatter = function() {
        try {
          const result = originalFormatter.call(this);
          if (result == null || result === undefined) return '';
          return String(result).trim() || '';
        } catch (e) {
          return '';
        }
      };
    }
    
    // ✅ CRÍTICO: Sanitizar dataLabels.formatter (pode retornar undefined)
    // Agora as funções já estão preservadas, mas garantimos que retornem strings válidas
    if (sanitized.dataLabels?.formatter && typeof sanitized.dataLabels.formatter === 'function') {
      const originalFormatter = sanitized.dataLabels.formatter;
      sanitized.dataLabels.formatter = function(val, opts) {
        try {
          // Chamar o formatter original com todos os argumentos
          const result = originalFormatter.call(this, val, opts);
          if (result == null || result === undefined) return '0min';
          const str = String(result).trim();
          return str || '0min';
        } catch (e) {
          console.warn('Erro no formatter de dataLabels:', e);
          return '0min';
        }
      };
    }
    
    // ✅ CRÍTICO: Sanitizar legend.labels.colors (pode ser array ou string)
    if (sanitized.legend?.labels?.colors) {
      const colors = sanitized.legend.labels.colors;
      if (Array.isArray(colors)) {
        sanitized.legend.labels.colors = colors.map(color => safeThemeValue(color, '#000000'));
      } else {
        sanitized.legend.labels.colors = safeThemeValue(colors, '#000000');
      }
    }
    
    // ✅ CRÍTICO: Sanitizar tooltip.theme (pode ser undefined)
    if (sanitized.tooltip) {
      sanitized.tooltip.theme = safeThemeValue(sanitized.tooltip.theme, 'light');
    }
    
    // ✅ CRÍTICO: Sanitizar chart.type (pode ser undefined)
    if (sanitized.chart) {
      sanitized.chart.type = safeThemeValue(sanitized.chart.type, 'bar');
      // Garantir que foreColor e fontFamily sejam sempre strings válidas
      if (!sanitized.chart.foreColor || typeof sanitized.chart.foreColor !== 'string') {
        sanitized.chart.foreColor = '#000000';
      }
      if (!sanitized.chart.fontFamily || typeof sanitized.chart.fontFamily !== 'string') {
        sanitized.chart.fontFamily = 'Arial, sans-serif';
      }
    }
    
    // ✅ CRÍTICO: Garantir que xaxis.categories seja sempre um array de strings válidas
    if (sanitized.xaxis?.categories) {
      sanitized.xaxis.categories = sanitized.xaxis.categories.map(cat => {
        if (!cat || typeof cat !== 'string') return '';
        return String(cat).trim() || '';
      }).filter(cat => cat !== '');
    }
    
    // ✅ CRÍTICO: Garantir que xaxis.labels.colors seja sempre uma string válida
    if (sanitized.xaxis?.labels?.style?.colors) {
      const colors = sanitized.xaxis.labels.style.colors;
      if (typeof colors !== 'string') {
        sanitized.xaxis.labels.style.colors = '#000000';
      } else {
        sanitized.xaxis.labels.style.colors = String(colors).trim() || '#000000';
      }
    }
    
    // ✅ CRÍTICO: Garantir que yaxis.labels.colors seja sempre uma string válida
    if (sanitized.yaxis?.labels?.style?.colors) {
      const colors = sanitized.yaxis.labels.style.colors;
      if (typeof colors !== 'string') {
        sanitized.yaxis.labels.style.colors = '#000000';
      } else {
        sanitized.yaxis.labels.style.colors = String(colors).trim() || '#000000';
      }
    }

    if (!sanitized.chart) {
      sanitized.chart = {};
    }
    
    // ✅ CRÍTICO: Sanitizar foreColor (pode ser undefined e causar toLowerCase)
    sanitized.chart.foreColor = safeThemeValue(sanitized.chart.foreColor, '#000000');
    
    // ✅ CRÍTICO: Sanitizar fontFamily (pode ser undefined)
    sanitized.chart.fontFamily = safeThemeValue(sanitized.chart.fontFamily, 'Arial, sans-serif');
    
    // ✅ CRÍTICO: Garantir que TODOS os campos do chart sejam strings válidas
    if (sanitized.chart.type) {
      sanitized.chart.type = safeThemeValue(sanitized.chart.type, 'bar');
    }
    if (sanitized.chart.toolbar) {
      // Garantir que toolbar não tenha valores undefined
      if (sanitized.chart.toolbar.tools) {
        sanitized.chart.toolbar.tools = sanitized.chart.toolbar.tools || {};
      }
    }
    
    // ✅ CRÍTICO: Sanitizar colors array (pode conter undefined)
    if (sanitized.colors && Array.isArray(sanitized.colors)) {
      sanitized.colors = sanitized.colors.map(color => {
        if (color == null || color === undefined || color === '') return '#000000';
        try {
          const str = String(color).trim();
          return str || '#000000';
        } catch (e) {
          return '#000000';
        }
      }).filter(color => color !== '' && color != null && color !== undefined);
    } else if (!sanitized.colors) {
      // Se não existe, não criar array vazio (deixar undefined)
      delete sanitized.colors;
    }
    
    // ✅ CRÍTICO: Sanitizar xaxis.labels.colors (pode ser array ou string)
    if (sanitized.xaxis?.labels) {
      if (!sanitized.xaxis.labels.colors) {
        sanitized.xaxis.labels.colors = '#000000';
      } else {
        const colors = sanitized.xaxis.labels.colors;
        if (Array.isArray(colors)) {
          sanitized.xaxis.labels.colors = colors.map(color => safeThemeValue(color, '#000000'));
        } else {
          sanitized.xaxis.labels.colors = safeThemeValue(colors, '#000000');
        }
      }
    }
    
    // ✅ CRÍTICO: Sanitizar yaxis.labels.colors (pode ser array ou string)
    if (sanitized.yaxis?.labels) {
      if (!sanitized.yaxis.labels.colors) {
        sanitized.yaxis.labels.colors = '#000000';
      } else {
        const colors = sanitized.yaxis.labels.colors;
        if (Array.isArray(colors)) {
          sanitized.yaxis.labels.colors = colors.map(color => safeThemeValue(color, '#000000'));
        } else {
          sanitized.yaxis.labels.colors = safeThemeValue(colors, '#000000');
        }
      }
    }
    
    return sanitized;
  } catch (e) {
    console.error('Erro ao sanitizar opções do ApexCharts:', e, options);
    return {};
  }
};

